import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  getDefaultMarketingConsentCopy,
  DRAFTKINGS_PRIVACY_POLICY_URL,
  MODELO_PRIVACY_NOTICE_URL,
  MODELO_USER_AGREEMENT_URL,
} from '@/constants/marketingConsentCopy';

export interface MarketingConsentModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onClose?: () => void;
  headerImageUrl?: string | null;
  headline?: string;
  subheadline?: string;
  checkboxPrompt?: string;
  eligibilityText?: string;
  footerText?: string;
  buttonText?: string;
  consentTextHtml?: string;
}

const MarketingConsentModal: React.FC<MarketingConsentModalProps> = ({
  isOpen,
  onAccept,
  onClose,
  headerImageUrl,
  headline,
  subheadline,
  checkboxPrompt,
  eligibilityText,
  footerText,
  buttonText,
  consentTextHtml,
}) => {
  const { t } = useTranslation();
  const defaults = getDefaultMarketingConsentCopy();

  const resolvedHeadline = headline ?? defaults.headline;
  const resolvedSubheadline = subheadline ?? defaults.subheadline;
  const resolvedCheckboxPrompt = checkboxPrompt ?? defaults.checkboxPrompt;
  const resolvedEligibilityText = eligibilityText ?? defaults.eligibilityText;
  const resolvedButtonText = buttonText ?? defaults.buttonText;

  const [isAgreed, setIsAgreed] = useState(false);

  useEffect(() => {
    if (isOpen) setIsAgreed(false);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
      data-test-id="marketing-consent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="marketing-consent-title"
    >
      <div className="relative flex max-h-[min(92dvh,760px)] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
            data-test-id="marketing-consent-close"
            aria-label={t('marketingConsent.a11y.close')}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
        <div className="relative h-[140px] shrink-0 overflow-hidden bg-[#0b1f4a]">
          {headerImageUrl ? (
            <img
              src={headerImageUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              data-test-id="marketing-consent-header-image"
            />
          ) : (
            <div
              className="flex h-full min-h-[140px] items-center justify-center bg-gradient-to-br from-[#0b1f4a] via-[#12356f] to-[#1d4f9c] px-6"
              data-test-id="marketing-consent-header-fallback"
            >
              <p className="text-center text-sm font-semibold uppercase tracking-wide text-white/90">
                {t('marketingConsent.brandFallback')}
              </p>
            </div>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-5">
          {consentTextHtml ? (
            <div
              id="marketing-consent-title"
              className="prose prose-sm max-w-none text-black [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-center [&_p]:text-sm [&_p]:leading-relaxed"
              dangerouslySetInnerHTML={{ __html: consentTextHtml }}
              data-test-id="marketing-consent-body-html"
            />
          ) : (
            <>
              <h2
                id="marketing-consent-title"
                className="text-center text-xl font-bold leading-tight text-black"
              >
                {resolvedHeadline}
              </h2>
              <p className="mt-2 text-center text-base font-bold text-black">
                {resolvedSubheadline}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-black">{resolvedCheckboxPrompt}</p>
            </>
          )}

          <div className="mt-4 flex items-start gap-3">
            <button
              type="button"
              onClick={() => setIsAgreed(current => !current)}
              className="mt-0.5 shrink-0"
              data-test-id="marketing-consent-checkbox"
              aria-pressed={isAgreed}
              aria-label={t('marketingConsent.a11y.consentCheckbox')}
            >
              <div
                className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${
                  isAgreed ? 'border-[#01A925] bg-[#01A925]' : 'border-gray-400 bg-white'
                }`}
              >
                {isAgreed ? <Check className="h-3.5 w-3.5 text-white" /> : null}
              </div>
            </button>
            <p className="text-sm leading-relaxed text-black">
              {t('marketingConsent.consentBodyBeforeLinks')}{' '}
              <a
                href={MODELO_PRIVACY_NOTICE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1d4ed8] underline"
              >
                {t('marketingConsent.privacyNotice')}
              </a>{' '}
              {t('marketingConsent.consentBodyBetweenLinks')}{' '}
              <a
                href={MODELO_USER_AGREEMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1d4ed8] underline"
              >
                {t('marketingConsent.userAgreement')}
              </a>
              {t('marketingConsent.consentBodyAfterLinks')}*
            </p>
          </div>

          {!consentTextHtml && (
            <p className="mt-4 text-sm font-medium leading-relaxed text-[#c8102e]">
              {resolvedEligibilityText}
            </p>
          )}

          <Button
            type="button"
            onClick={onAccept}
            disabled={!isAgreed}
            className={`mt-5 w-full rounded-lg py-6 text-base font-bold uppercase ${
              isAgreed
                ? 'bg-[#01A925] text-white hover:bg-[#018A1E]'
                : 'cursor-not-allowed bg-gray-300 text-gray-500'
            }`}
            data-test-id="marketing-consent-accept"
          >
            {resolvedButtonText}
          </Button>

          <p className="mt-4 text-xs leading-relaxed text-black">
            {footerText ? (
              footerText
            ) : (
              <>
                {t('marketingConsent.footerBeforePrivacy')}{' '}
                <a
                  href={DRAFTKINGS_PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1d4ed8] underline"
                >
                  {t('marketingConsent.privacyPolicy')}
                </a>
                {t('marketingConsent.footerAfterPrivacy')}{' '}
                <a href="mailto:privacy@cbrands.com" className="text-[#1d4ed8] underline">
                  privacy@cbrands.com
                </a>
                .
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MarketingConsentModal;
